import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { User, LogOut, LayoutDashboard, Settings, ChevronDown, Menu, Shield, X } from 'lucide-react';
import { ThemeToggle } from '../ThemeToggle/ThemeToggle';

export const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // New SVG wordmark — perfectly crisp at any size, theme-aware
  const logoSvg = theme === 'light' ? '/servio-logo-light.svg' : '/servio-logo-dark.svg';

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <nav className="sticky top-0 z-50 glass" data-testid="navbar">
      <div className="section-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo — clean SVG wordmark */}
          <Link to="/" className="flex items-center group" data-testid="logo-link" onClick={closeMobileMenu}>
            <img
              src={logoSvg}
              alt="Servio"
              className="h-7 md:h-9 w-auto"
              data-testid="logo-image"
              width="160"
              height="40"
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/search" 
              className="text-slate-400 hover:text-white font-medium transition-colors"
              data-testid="search-link"
            >
              Find Servers
            </Link>
            <Link 
              to="/pricing#features" 
              className="text-slate-400 hover:text-white font-medium transition-colors"
              data-testid="features-link"
            >
              Features
            </Link>
            <Link 
              to="/pricing#plans" 
              className="text-slate-400 hover:text-white font-medium transition-colors"
              data-testid="pricing-link"
            >
              Pricing
            </Link>
            <Link 
              to="/pricing#faq" 
              className="text-slate-400 hover:text-white font-medium transition-colors"
              data-testid="faq-link"
            >
              FAQs
            </Link>
            <Link 
              to="/resources" 
              className="text-slate-400 hover:text-white font-medium transition-colors"
              data-testid="resources-link"
            >
              Resources
            </Link>
          </div>

          {/* Desktop Auth + Mobile Menu Button */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Theme Toggle - Desktop */}
            <div className="hidden md:block">
              <ThemeToggle variant="icon" />
            </div>
            
            {/* Desktop Auth */}
            <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 h-10 px-3 hover:bg-slate-800/60" data-testid="user-menu-trigger">
                    {user?.photo_url ? (
                      <img 
                        src={user.photo_url} 
                        alt={user?.full_name} 
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                        <span className="text-slate-900 text-sm font-semibold">
                          {user?.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm font-medium text-slate-300">
                      {user?.full_name?.split(' ')[0]}
                    </span>
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-700">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2 text-slate-300 focus:bg-slate-800 focus:text-white" data-testid="dashboard-link">
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard?tab=settings&_t=' + Date.now())}
                    className="flex items-center gap-2 text-slate-300 focus:bg-slate-800 focus:text-white cursor-pointer" 
                    data-testid="settings-link"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  {(user?.role === 'admin' || user?.role === 'moderator') && (
                    <>
                      <DropdownMenuSeparator className="bg-slate-700" />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 text-teal-400 focus:bg-slate-800 focus:text-teal-300" data-testid="admin-link">
                          <Shield className="h-4 w-4" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="flex items-center gap-2 text-red-400 focus:bg-slate-800 focus:text-red-400"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button asChild variant="ghost" className="text-slate-400 hover:text-white hover:bg-slate-800/60 font-medium" data-testid="login-btn">
                  <Link to="/login">Log in</Link>
                </Button>
                <Button asChild className="btn-accent px-6" data-testid="signup-btn">
                  <Link to="/register">Get Started</Link>
                </Button>
              </>
            )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-400 hover:text-white transition-colors"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-800/50 py-4 space-y-1 animate-fade-in">
            <Link 
              to="/search" 
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-slate-300 hover:bg-slate-800/60 hover:text-white rounded-lg transition-colors"
            >
              Find Servers
            </Link>
            <Link 
              to="/pricing#features" 
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-slate-300 hover:bg-slate-800/60 hover:text-white rounded-lg transition-colors"
            >
              Features
            </Link>
            <Link 
              to="/pricing#plans" 
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-slate-300 hover:bg-slate-800/60 hover:text-white rounded-lg transition-colors"
            >
              Pricing
            </Link>
            <Link 
              to="/pricing#faq" 
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-slate-300 hover:bg-slate-800/60 hover:text-white rounded-lg transition-colors"
            >
              FAQs
            </Link>
            <Link 
              to="/resources" 
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-slate-300 hover:bg-slate-800/60 hover:text-white rounded-lg transition-colors"
            >
              Resources
            </Link>
            
            {/* Mobile Theme Toggle */}
            <div className="px-4 py-3">
              <ThemeToggle variant="full" />
            </div>
            
            {!isAuthenticated && (
              <div className="pt-4 mt-4 border-t border-slate-800/50 space-y-2 px-4">
                <Button asChild className="w-full btn-secondary" data-testid="mobile-login-btn">
                  <Link to="/login" onClick={closeMobileMenu}>Log in</Link>
                </Button>
                <Button asChild className="w-full btn-accent" data-testid="mobile-signup-btn">
                  <Link to="/register" onClick={closeMobileMenu}>Get Started</Link>
                </Button>
              </div>
            )}
            
            {isAuthenticated && (
              <div className="pt-4 mt-4 border-t border-slate-800/50 space-y-1">
                <Link 
                  to="/dashboard" 
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:bg-slate-800/60 hover:text-white rounded-lg transition-colors"
                >
                  <LayoutDashboard className="h-5 w-5" />
                  Dashboard
                </Link>
                <button 
                  onClick={() => { closeMobileMenu(); navigate('/dashboard?tab=settings&_t=' + Date.now()); }}
                  className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:bg-slate-800/60 hover:text-white rounded-lg transition-colors w-full text-left"
                >
                  <Settings className="h-5 w-5" />
                  Settings
                </button>
                {(user?.role === 'admin' || user?.role === 'moderator') && (
                  <Link 
                    to="/admin" 
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 text-teal-400 hover:bg-slate-800/60 rounded-lg transition-colors"
                  >
                    <Shield className="h-5 w-5" />
                    Admin Panel
                  </Link>
                )}
                <button 
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-red-400 hover:bg-slate-800/60 rounded-lg transition-colors text-left"
                >
                  <LogOut className="h-5 w-5" />
                  Log out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export const Footer = () => {
  const { theme } = useTheme();
  const logoSvg = theme === 'light' ? '/servio-logo-light.svg' : '/servio-logo-dark.svg';
  
  return (
    <footer className="border-t border-slate-800/50 py-16" data-testid="footer">
      <div className="section-container">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center">
              <img
                src={logoSvg}
                alt="Servio"
                className="h-8 w-auto"
                data-testid="footer-logo-image"
                width="180"
                height="40"
                loading="lazy"
              />
            </div>
            <p className="mt-4 text-slate-500 text-sm leading-relaxed">
              The modern standard for finding trusted process servers nationwide.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">Platform</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/search" className="text-slate-400 hover:text-teal-400 transition-colors">Find Servers</Link></li>
              <li><Link to="/pricing#features" className="text-slate-400 hover:text-teal-400 transition-colors">Features</Link></li>
              <li><Link to="/pricing#plans" className="text-slate-400 hover:text-teal-400 transition-colors">Pricing</Link></li>
              <li><Link to="/pricing#faq" className="text-slate-400 hover:text-teal-400 transition-colors">FAQs</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">Resources</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/resources" className="text-slate-400 hover:text-teal-400 transition-colors">Learning Center</Link></li>
              <li><Link to="/register" className="text-slate-400 hover:text-teal-400 transition-colors">Join as Server</Link></li>
              <li><Link to="/login" className="text-slate-400 hover:text-teal-400 transition-colors">Log In</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">Legal</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/privacy" className="text-slate-400 hover:text-teal-400 transition-colors">Privacy</Link></li>
              <li><Link to="/terms" className="text-slate-400 hover:text-teal-400 transition-colors">Terms</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-teal-400 transition-colors">Contact Us</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800/50 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Servio. All rights reserved.
          </p>
          <p className="text-sm text-slate-600 font-mono">
            Built for legal professionals
          </p>
        </div>
      </div>
    </footer>
  );
};
